import { useAccessControl } from "@/hooks/useAccessControl";
import useTabStore from "@/stores/useTabStore";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import { PlusIcon } from "@heroicons/react/20/solid";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { PopoverPanel } from "@headlessui/react";
import clsx from "clsx";
import {
  internalOnlyWidgetGroups,
  widgetGroups,
  widgetGroupTaglines,
  type Widget,
} from "@/utils/widgets";
import { useCallback, useMemo, useRef, useState } from "react";
import { useConfirm } from "@/ui-components/ConfirmDialog";
import toast from "react-hot-toast";
import {
  createLayoutExportData,
  downloadLayoutJson,
  readLayoutFiles,
  remapWidgetIds,
} from "@/utils/layoutExportImport";
import { useNewsquawkOwnedIds } from "@/hooks/useNewsquawkOwnedIds";
import { useGateOnUpdate } from "@/hooks/useGateOnUpdate";

/** Short labels for the pillar chip row (full names live on the group headers). */
const PILLAR_CHIP_LABELS: Record<string, string> = {
  "Human Intelligence": "Newsquawk",
  "Curated External Intelligence": "Curated External Feeds",
  "Equities Intelligence Suite": "Equities Suite",
  "External Widgets": "Third Party Widgets",
  "Internal Only": "Internal",
};

/** Sentinel for the inventory chip — "Active" is not a widget group key. */
const ACTIVE_KEY = "active";

/**
 * When listing widgets in flat views (Active Widgets), prepend this to the
 * label for widgets from this group. Pillar context is lost in a flat list,
 * so Newsquawk-original feeds reclaim their brand prefix here.
 */
const PILLAR_FLAT_PREFIX: Record<string, string> = {
  "Human Intelligence": "Newsquawk ",
};

const matchesQuery = (widget: Widget, q: string) => {
  if (!q) return true;
  const haystack = [
    widget.displayName ?? widget.name,
    widget.name,
    widget.description ?? "",
    ...(widget.keywords ?? []),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

export function WidgetdropdownMenu({ ...props }: { className?: string }) {
  const confirm = useConfirm();
  const gate = useGateOnUpdate();
  const {
    authenticated,
    isSubscriber,
    isInternalUser: internalUser,
    hasAccessToWidget,
  } = useAccessControl();

  const newsquawkIds = useNewsquawkOwnedIds();

  const resetLayouts = useTabStore((state) => state.resetLayouts);
  const activeTabId = useTabStore((state) => state.activeTabId);
  const activeTab = useTabStore((state) =>
    state.tabs.find((tab) => tab.id === state.activeTabId)
  );
  const addWidget = useTabStore((state) => state.addWidget);
  const removeWidget = useTabStore((state) => state.removeWidget);
  const importTab = useTabStore((state) => state.importTab);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  // Default to the "Active" inventory chip. User can drill into any pillar.
  const [activePillar, setActivePillar] = useState<string>(ACTIVE_KEY);

  const handleAddWidget = (widgetId: string) => {
    addWidget(
      activeTabId,
      `${widgetId}-${Math.random().toString(36).substr(2, 9)}`
    );
  };

  // Remove one instance of the widget from the active tab. With ×N rows,
  // multiple clicks peel off instances one at a time (most recent first
  // would feel surprising, so just take the first match — order on the grid
  // is not the user's mental model anyway).
  const handleRemoveWidget = (widgetId: string) => {
    const compositeId = activeTab?.enabledWidgets.find((id) =>
      id.startsWith(widgetId)
    );
    if (compositeId) removeWidget(activeTabId, compositeId);
  };

  const handleExport = () => {
    if (!activeTab) return;
    const data = createLayoutExportData(activeTab, newsquawkIds);
    const safeTitle = activeTab.title.replace(/[^a-zA-Z0-9-_]/g, "_");
    const date = new Date().toISOString().slice(0, 10);
    downloadLayoutJson(data, `newsquawk-layout-${safeTitle}-${date}.json`);
    toast.success("Layout exported");
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length === 0) return;

    const { validTabs, errors } = await readLayoutFiles(files);

    if (errors.length > 0) {
      toast.error(
        `Skipped ${errors.length} invalid file(s):\n${errors.join("\n")}`
      );
    }

    if (validTabs.length === 0) return;

    const tabNames = validTabs.map((t) => t.tabTitle).join(", ");
    const ok = await confirm({ title: "Import Tabs", message: `Import ${validTabs.length} tab(s) (${tabNames})? They will be added as new tabs.`, confirmLabel: "Import", variant: "info" });
    if (!ok) return;

    for (const data of validTabs) {
      const { enabledWidgets, layout, widgetFilters } = remapWidgetIds(data);
      importTab(data.tabTitle, enabledWidgets, layout, widgetFilters);
    }

    toast.success(`Imported ${validTabs.length} tab(s)`);
  };

  const countWidgetInstances = useCallback(
    (widgetId: string) => {
      let count = 0;
      activeTab?.enabledWidgets.forEach((_widgetId) => {
        if (_widgetId.startsWith(widgetId)) count++;
      });
      return count;
    },
    [activeTab?.enabledWidgets]
  );

  const widgetVisible = useCallback(
    (widget: Widget) => {
      if (widget.internalOnly && !internalUser) return false;
      if (widget.hideFromMenuWhenLoggedOut && !authenticated) return false;
      return true;
    },
    [internalUser, authenticated]
  );

  const accessibleGroupKeys = useMemo(
    () =>
      Object.keys(widgetGroups).filter(
        (k) => !(internalOnlyWidgetGroups.includes(k) && !internalUser)
      ),
    [internalUser]
  );

  // Per-chip count after visibility + current search. Updates live.
  const pillarCounts = useMemo(() => {
    const out: Record<string, number> = { [ACTIVE_KEY]: 0 };
    for (const key of accessibleGroupKeys) {
      let count = 0;
      let activeCount = 0;
      for (const widget of widgetGroups[key]) {
        if (!widgetVisible(widget)) continue;
        if (!matchesQuery(widget, q)) continue;
        count++;
        if (countWidgetInstances(widget.id) > 0) activeCount++;
      }
      out[key] = count;
      out[ACTIVE_KEY] += activeCount;
    }
    return out;
  }, [accessibleGroupKeys, widgetVisible, q, countWidgetInstances]);

  // Active inventory as a flat list (post visibility + post query).
  // Active view skips group titles — it's just the widgets on this tab.
  // Each entry carries a precomputed `label` so flat-list views can reapply
  // any pillar-aware prefix (e.g. "Newsquawk ") that was stripped from
  // displayName for the pillar context.
  const activeWidgetsFlat = useMemo(() => {
    const out: { widget: Widget; count: number; label: string }[] = [];
    for (const key of accessibleGroupKeys) {
      const prefix = PILLAR_FLAT_PREFIX[key] ?? "";
      for (const widget of widgetGroups[key]) {
        if (!widgetVisible(widget)) continue;
        if (!matchesQuery(widget, q)) continue;
        const c = countWidgetInstances(widget.id);
        if (c > 0) {
          const label = `${prefix}${widget.displayName ?? widget.name}`;
          out.push({ widget, count: c, label });
        }
      }
    }
    return out;
  }, [accessibleGroupKeys, widgetVisible, q, countWidgetInstances]);

  // Pillar view groups (when a non-Active chip is selected).
  const visibleGroups = useMemo(() => {
    if (activePillar === ACTIVE_KEY) return [];
    return accessibleGroupKeys
      .filter((key) => key === activePillar)
      .map((key) => ({
        key,
        items: widgetGroups[key]
          .filter((w) => widgetVisible(w) && matchesQuery(w, q))
          .map((widget) => ({
            widget,
            count: countWidgetInstances(widget.id),
          })),
      }))
      .filter((group) => group.items.length > 0);
  }, [
    accessibleGroupKeys,
    activePillar,
    widgetVisible,
    q,
    countWidgetInstances,
  ]);

  const isActiveChip = activePillar === ACTIVE_KEY;
  const totalActive = pillarCounts[ACTIVE_KEY] ?? 0;

  if (!activeTab) return null;

  /**
   * Render a single widget row.
   * - `actionMode: "add"` → + button (used in pillar / catalog views)
   * - `actionMode: "remove"` → × button (used in Active Widgets view)
   */
  const renderWidgetRow = (
    widget: Widget,
    count: number,
    actionMode: "add" | "remove",
    labelOverride?: string
  ) => {
    const hasAccess = hasAccessToWidget(widget);
    const isAddDisabled = authenticated && isSubscriber && !hasAccess;
    const showLockUnauth =
      !authenticated && !widget.unAuthAccessible && !widget.demoAccessible;
    const showLockNoRole = authenticated && isSubscriber && !hasAccess;

    const label = labelOverride ?? widget.displayName ?? widget.name;

    // In add mode the entire row is the click target — the + is just a cue.
    // In remove mode the row stays passive; only the explicit × button is
    // clickable (destructive action — no accidental row-click removes).
    const isRowAddInteractive = actionMode === "add" && !isAddDisabled;
    const addAction = isRowAddInteractive
      ? gate(() => handleAddWidget(widget.id))
      : undefined;

    return (
      <div
        key={widget.id}
        role={isRowAddInteractive ? "button" : undefined}
        tabIndex={isRowAddInteractive ? 0 : undefined}
        aria-label={isRowAddInteractive ? `Add ${label}` : undefined}
        onClick={addAction}
        onKeyDown={
          isRowAddInteractive
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  addAction?.();
                }
              }
            : undefined
        }
        title={
          actionMode === "add" && isAddDisabled
            ? "Subscription required to add this widget"
            : isRowAddInteractive
              ? "Add widget"
              : undefined
        }
        className={clsx(
          "group -mx-1.5 rounded px-1.5 py-1.5 transition-colors focus:outline-none",
          isAddDisabled
            ? "cursor-not-allowed opacity-50"
            : "hover:bg-gray-700/40 focus-within:bg-gray-700/40 focus-visible:bg-gray-700/40",
          isRowAddInteractive && "cursor-pointer"
        )}
      >
        <div className="flex items-center gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {showLockUnauth && (
              <LockClosedIcon className="size-3.5 flex-shrink-0 text-red-400" />
            )}
            {showLockNoRole && (
              <LockClosedIcon className="size-3.5 flex-shrink-0 text-terminal-orange" />
            )}

            <span className="text-sm font-semibold text-white">{label}</span>

            {/* ×N only when count >= 2 (×1 is implicit and was noisy) */}
            {count >= 2 && (
              <span
                title={`${count} on this tab`}
                className="text-xs font-medium tabular-nums text-gray-300"
              >
                ×{count}
              </span>
            )}

          </div>

          {actionMode === "add" ? (
            <PlusIcon
              aria-hidden
              className={clsx(
                "size-4 flex-shrink-0 transition-colors",
                isAddDisabled
                  ? "text-gray-600"
                  : "text-squawk-blue group-hover:text-squawk-blue/80"
              )}
            />
          ) : (
            <button
              type="button"
              onClick={gate(() => handleRemoveWidget(widget.id))}
              title={
                count >= 2
                  ? `Remove one (×${count} on this tab)`
                  : "Remove widget"
              }
              aria-label="Remove widget"
              className="flex-shrink-0 rounded p-1.5 text-gray-500 transition-colors hover:bg-red-500/10 hover:text-red-400 focus-visible:bg-red-500/10 focus-visible:text-red-400 focus:outline-none"
            >
              <XMarkIcon className="size-4" />
            </button>
          )}
        </div>

        {widget.description && (
          <p className="mt-0.5 text-xs text-gray-400">{widget.description}</p>
        )}
      </div>
    );
  };

  /**
   * Render a pillar's widget rows with subgroup dividers.
   * The pillar chip already conveys which pillar you're in, so no group
   * title or tagline is repeated here — it would just be noise.
   */
  const renderGroup = (
    key: string,
    items: { widget: Widget; count: number }[]
  ) => {
    let lastSubgroup: string | undefined;

    return (
      <div className="break-inside-avoid-column" key={key}>
        {items.map(({ widget, count }) => {
          const showSubgroup =
            widget.subgroup && widget.subgroup !== lastSubgroup;
          if (widget.subgroup) lastSubgroup = widget.subgroup;
          return (
            <div key={widget.id}>
              {showSubgroup && (
                <div className="flex items-center gap-2 px-0.5 pb-1 pt-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-300">
                    {widget.subgroup}
                  </span>
                  <span className="h-px flex-1 bg-gray-700/70" />
                </div>
              )}
              {renderWidgetRow(widget, count, "add")}
            </div>
          );
        })}
      </div>
    );
  };

  // Pillar view uses columns when search returns matches from multiple pillars.
  const pillarViewUsesColumns = !isActiveChip && visibleGroups.length > 1;

  return (
    <>
      {/* File input lives outside the panel so it persists after the dropdown closes */}
      <input
        type="file"
        accept=".json"
        multiple
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
      />
      <PopoverPanel
        transition
        anchor={{ to: "bottom end" }}
        className={clsx(
          "z-50 transition duration-100 ease-out data-[closed]:opacity-0",
          props.className
        )}
      >
        {({ close }) => (
          <div className="my-2 flex max-h-[80vh] w-[calc(100vw-1rem)] max-w-[52rem] flex-col overflow-hidden rounded-lg border border-gray-700/50 bg-gray-800 text-xs text-gray-200 shadow-xl sm:w-[42rem] lg:w-[52rem]">
            {/* Chips */}
            <div className="shrink-0 border-b border-gray-700/30 bg-gray-800/50 px-3 py-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <PillarChip
                  label="Active Widgets"
                  count={totalActive}
                  active={isActiveChip}
                  onClick={() => setActivePillar(ACTIVE_KEY)}
                />
                <span className="mx-1 h-4 w-px self-center bg-gray-700/60" />
                {accessibleGroupKeys.map((key) => (
                  <PillarChip
                    key={key}
                    label={PILLAR_CHIP_LABELS[key] ?? key}
                    count={pillarCounts[key] ?? 0}
                    active={activePillar === key}
                    onClick={() => setActivePillar(key)}
                  />
                ))}
              </div>
            </div>

            {/* Search */}
            <div className="shrink-0 border-b border-gray-700/30 px-3 py-2">
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search widgets..."
                  className="w-full rounded border border-gray-700 bg-gray-900 py-1.5 pl-8 pr-8 text-xs text-gray-200 placeholder:text-gray-500 focus:border-squawk-blue focus:outline-none"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-500 transition-colors hover:text-gray-300"
                  >
                    <XMarkIcon className="size-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Body */}
            <div
              className={clsx(
                "min-h-0 flex-1 overflow-y-auto p-3 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-700",
                pillarViewUsesColumns && "gap-5 sm:columns-2 lg:columns-3"
              )}
            >
              {isActiveChip ? (
                // ── Active inventory ─────────────────────────────────────
                !q && totalActive === 0 ? (
                  // Empty inventory + no search → catalog overview as helpful empty state
                  <PillarOverview
                    pillarKeys={accessibleGroupKeys}
                    pillarCounts={pillarCounts}
                    onSelect={setActivePillar}
                  />
                ) : totalActive === 0 ? (
                  // Searching but nothing on the tab matches → point to where matches live
                  <SearchEmptyHint
                    query={query}
                    pillarKeys={accessibleGroupKeys}
                    pillarCounts={pillarCounts}
                    onSelect={setActivePillar}
                  />
                ) : (
                  // Flat row list — no group titles in Active view.
                  // Each row gets its pillar-aware label so Newsquawk-original
                  // feeds read as "Newsquawk Headlines" etc. without pillar context.
                  <div>
                    {activeWidgetsFlat.map(({ widget, count, label }) =>
                      renderWidgetRow(widget, count, "remove", label)
                    )}
                  </div>
                )
              ) : (
                // ── Pillar view (with + buttons) ─────────────────────────
                <>
                  {visibleGroups.length === 0 && (
                    <p className="text-xs text-gray-500">
                      No widgets in this pillar match “{query}”.
                    </p>
                  )}
                  {visibleGroups.map(({ key, items }) =>
                    renderGroup(key, items)
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-gray-700/50 bg-gray-800/50 px-3 py-2.5 sm:gap-4 sm:px-4 sm:py-3">
              <button
                className="text-xs bg-transparent text-squawk-blue inline-flex items-center gap-1"
                onClick={() => {
                  handleExport();
                  close();
                }}
              >
                <ArrowUpTrayIcon className="size-3.5" />
                <span className="sm:hidden">Export</span>
                <span className="hidden sm:inline">Export Layout</span>
              </button>
              <button
                className="text-xs bg-transparent text-squawk-blue inline-flex items-center gap-1"
                onClick={() => {
                  handleImportClick();
                  close();
                }}
              >
                <ArrowDownTrayIcon className="size-3.5" />
                <span className="sm:hidden">Import</span>
                <span className="hidden sm:inline">Import Layouts</span>
              </button>
              <button
                className="text-xs border-squawk-blue bg-transparent px-3 text-squawk-blue sm:px-12"
                onClick={async () => {
                  close();
                  const ok = await confirm({ title: "Reset Tab Layout", message: "This will reset the current tab to its default widget layout. Any custom arrangement will be lost.", confirmLabel: "Reset", variant: "warning" });
                  if (ok) resetLayouts(activeTabId);
                }}
                id="btn-reset"
              >
                <span className="sm:hidden">Reset Layout</span>
                <span className="hidden sm:inline">Reset Tab to Default Layout</span>
              </button>
            </div>
          </div>
        )}
      </PopoverPanel>
    </>
  );
}

// ── Pillar overview (empty Active state) ────────────────────────────────

function PillarOverview({
  pillarKeys,
  pillarCounts,
  onSelect,
}: {
  pillarKeys: string[];
  pillarCounts: Record<string, number>;
  onSelect: (pillarKey: string) => void;
}) {
  return (
    <div>
      <p className="mb-2 px-1 text-xs text-gray-400">
        Nothing on this tab yet. Pick a pillar to browse:
      </p>
      {pillarKeys.map((key) => {
        const count = pillarCounts[key] ?? 0;
        const tagline = widgetGroupTaglines[key];
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(key)}
            className="group flex w-full items-center gap-3 border-b border-gray-700/30 px-2 py-3 text-left transition-colors last:border-b-0 hover:bg-gray-700/40 focus:bg-gray-700/40 focus:outline-none"
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-100">{key}</div>
              {tagline && (
                <div className="mt-0.5 text-xs text-gray-400">{tagline}</div>
              )}
            </div>
            <span className="flex-shrink-0 rounded bg-squawk-blue/20 px-2 py-0.5 text-2xs font-medium tabular-nums text-squawk-blue">
              {count}
            </span>
            <ChevronRightIcon className="size-4 flex-shrink-0 text-gray-500 transition-colors group-hover:text-gray-300" />
          </button>
        );
      })}
    </div>
  );
}

// ── Search empty hint (Active + search with no inventory match) ─────────

function SearchEmptyHint({
  query,
  pillarKeys,
  pillarCounts,
  onSelect,
}: {
  query: string;
  pillarKeys: string[];
  pillarCounts: Record<string, number>;
  onSelect: (pillarKey: string) => void;
}) {
  const elsewhere = pillarKeys.filter((k) => (pillarCounts[k] ?? 0) > 0);
  return (
    <div className="px-1 py-3 text-xs text-gray-400">
      <p>
        Nothing on this tab matches “
        <span className="text-gray-200">{query}</span>”.
      </p>
      {elsewhere.length > 0 && (
        <p className="mt-2">
          <span className="text-gray-500">Try a pillar: </span>
          {elsewhere.map((k, i) => (
            <span key={k}>
              {i > 0 && <span className="text-gray-600"> · </span>}
              <button
                type="button"
                onClick={() => onSelect(k)}
                className="text-squawk-blue transition-colors hover:text-squawk-blue/80 focus:outline-none"
              >
                {PILLAR_CHIP_LABELS[k] ?? k}{" "}
                <span className="text-2xs text-squawk-blue/70">
                  ({pillarCounts[k]})
                </span>
              </button>
            </span>
          ))}
        </p>
      )}
      {elsewhere.length === 0 && (
        <p className="mt-1 text-gray-500">No matches in the catalog either.</p>
      )}
    </div>
  );
}

// ── Pillar chip ─────────────────────────────────────────────────────────

function PillarChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  const disabled = count === 0 && !active;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs transition-colors focus:outline-none",
        active
          ? "border-squawk-blue/30 bg-squawk-blue/20 text-squawk-blue"
          : disabled
            ? "cursor-not-allowed border-gray-700/40 bg-gray-800 text-gray-600"
            : "border-gray-700/50 bg-gray-800 text-gray-400 hover:border-gray-600 hover:text-gray-200"
      )}
    >
      <span>{label}</span>
      <span
        className={clsx(
          "text-2xs tabular-nums",
          active ? "text-squawk-blue/80" : "opacity-60"
        )}
      >
        {count}
      </span>
    </button>
  );
}
